import { BigBashEvent, Game, GamePoint, Team } from "@/types/types";

class SmallHelpers {
	public calculateRoundsCount(
		gamePoints: GamePoint[],
		gameId: string,
		defaultRounds: number = 3
	): number {
		const relevantGamePoints = gamePoints.filter(gp => gp.game === gameId);

		let highestRoundIndex = -1;

		for (const gamePoint of relevantGamePoints) {
			for (const round of gamePoint.rounds) {
				if (round.round > highestRoundIndex) {
					highestRoundIndex = round.round;
				}
			}
		}

		const calculatedRounds = highestRoundIndex >= 0 ? highestRoundIndex + 1 : defaultRounds;

		return Math.max(calculatedRounds, defaultRounds);
	}

	public splitPlayerNames(playerNames: string): string[] {
		return playerNames.split(/[,\n;]+/).map(name => name.trim()).filter(name => name !== '' && name.length > 0);
	}

	public isTeamInput(additionalIdentifier: string): boolean {
		return additionalIdentifier?.startsWith('team') || false;
	}

	public isCustomerInput(additionalIdentifier: string): boolean {
		return additionalIdentifier?.startsWith('customer') || false;
	}

	public getTeamIndexFromAdditionalIdentifier(additionalIdentifier: string): number {
		const teamIndex = parseInt(additionalIdentifier?.split('-')[1] || '0');
		if (isNaN(teamIndex) || teamIndex < 0) {
			console.warn(`Invalid team index: ${additionalIdentifier}`);
			return -1;
		}
		return teamIndex;
	}

	public generateTeamColor(teamIndex: number): string {
		const root = getComputedStyle(document.documentElement);
		const colors = [
			root.getPropertyValue("--color-sky").trim(),
			root.getPropertyValue("--color-pink").trim(),
			root.getPropertyValue("--color-lemon").trim(),
			root.getPropertyValue("--color-sand").trim(),
			root.getPropertyValue("--color-mint").trim(),
		];

		const colorIndex = teamIndex % 5;
		const patternType = Math.floor(teamIndex / 5) % 5;
		const baseColor = colors[colorIndex];

		if (patternType === 0) {
			return baseColor;
		} else if (patternType === 1) {
			return `${baseColor}-circles`;
		} else if (patternType === 2) {
			return `${baseColor}-stripes`;
		} else if (patternType === 3) {
			return `${baseColor}-crosshatch`;
		} else {
			return `${baseColor}-grid`;
		}
	}

	public getTeamColor(teamColor: string) {
		const color = teamColor || '#ff6600';

		if (color.endsWith('-circles')) {
			const baseColor = color.replace('-circles', '');
			return {
				background: baseColor,
				backgroundImage: 'radial-gradient(circle, white 2px, transparent 2px)',
				backgroundSize: '12px 12px'
			};
		} else if (color.endsWith('-stripes')) {
			const baseColor = color.replace('-stripes', '');
			return {
				background: `repeating-linear-gradient(45deg, ${baseColor}, ${baseColor} 6px, white 6px, white 8px)`
			};
		} else if (color.endsWith('-crosshatch')) {
			const baseColor = color.replace('-crosshatch', '');
			return {
				background: baseColor,
				backgroundImage: [
					'repeating-linear-gradient(45deg, rgba(255,255,255,0.5), rgba(255,255,255,0.5) 1.5px, transparent 1.5px, transparent 8px)',
					'repeating-linear-gradient(-45deg, rgba(255,255,255,0.5), rgba(255,255,255,0.5) 1.5px, transparent 1.5px, transparent 8px)',
				].join(', '),
			};
		} else if (color.endsWith('-grid')) {
			const baseColor = color.replace('-grid', '');
			return {
				background: baseColor,
				backgroundImage: [
					'repeating-linear-gradient(0deg, rgba(255,255,255,0.5), rgba(255,255,255,0.5) 1.5px, transparent 1.5px, transparent 10px)',
					'repeating-linear-gradient(90deg, rgba(255,255,255,0.5), rgba(255,255,255,0.5) 1.5px, transparent 1.5px, transparent 10px)',
				].join(', '),
			};
		} else {
			return {
				background: color
			};
		}
	}

	private _hasGamePointsData(event: BigBashEvent): boolean {
		return !!(event && event.game_points);
	}

	private _hasGamesData(event: BigBashEvent): boolean {
		return !!(event && event.games);
	}

	private _hasTeamsData(event: BigBashEvent): boolean {
		return !!(event && event.teams);
	}

	public getPlayedGames(event: BigBashEvent): string[] {
		if (!this._hasGamePointsData(event)) {
			return [];
		}

		const uniqueGameIds = this._extractUniqueGameIds(event.game_points);
		return Array.from(uniqueGameIds);
	}

	private _extractUniqueGameIds(gamePoints: GamePoint[]): Set<string> {
		const uniqueGameIds = new Set<string>();
		gamePoints.forEach(gamePoint => {
			uniqueGameIds.add(gamePoint.game);
		});
		return uniqueGameIds;
	}

	public getGameByGameId(event: BigBashEvent, gameId: string): Game | null {
		if (!this._hasGamesData(event)) {
			return null;
		}

		return this._findGameById(event.games, gameId);
	}

	public getTeamByTeamId(event: BigBashEvent, teamId: string): Team | null {
		if (!this._hasTeamsData(event)) {
			return null;
		}

		return this._findTeamById(event.teams, teamId);
	}

	private _findGameById(games: Game[], gameId: string): Game | null {
		const game = games.find(game => game.id === gameId);
		return game || null;
	}

	private _findTeamById(teams: Team[], teamId: string): Team | null {
		const team = teams.find(team => team.id === teamId);
		return team || null;
	}

	public getPointsForRank(rank: number, placementPointsMapping: number[]): number {
		return rank < placementPointsMapping.length ? placementPointsMapping[rank] : 0;
	}

	public getGamePointsForGame(event: BigBashEvent, gameId: string) {
		return event.game_points.filter(gamePoint => gamePoint.game === gameId);
	}
}

export default SmallHelpers;